import * as React from "react";
import { useParams, Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import {
  CalendarClock, MessageSquare, Columns3, ClipboardList, UserCog, Wallet, BookOpen, CheckCircle2, XCircle,
} from "lucide-react";

const ICONS = {
  bookings: CalendarClock, comms: MessageSquare, boards: Columns3, rotas: ClipboardList,
  hr: UserCog, pay: Wallet, books: BookOpen,
};

/**
 * Single component for all 7 module stub pages. Verifies module gate by
 * hitting `_ping` — the exact route future feature routes will guard.
 */
export default function ModuleStub() {
  const { companyId, moduleId } = useParams();
  const { catalog, entitlements, has } = useCompany();
  const mod = catalog?.modules?.find((m) => m.id === moduleId);
  const [ping, setPing] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!companyId || !moduleId) return;
    setLoading(true); setError(null); setPing(null);
    api.get(`/companies/${companyId}/${moduleId}/_ping`)
      .then(({ data }) => setPing(data))
      .catch((e) => setError({
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
      }))
      .finally(() => setLoading(false));
  }, [companyId, moduleId]);

  if (catalog && !mod) return <Navigate to={`/c/${companyId}`} replace />;

  const Icon = ICONS[moduleId] || Columns3;
  const enabled = !!entitlements?.modules?.[moduleId];
  const viewCap = mod?.view_capability;
  const canView = has(viewCap || "");

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1000px]" data-testid={`module-stub-${moduleId}`}>
      <div className="micro-label">Module</div>
      <div className="mt-1 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${enabled ? 'bg-accent text-brand-violet' : 'bg-brand-hush text-brand-faint'}`}>
          <Icon size={22} />
        </div>
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-brand-ink">{mod?.label || moduleId}</h1>
          <div className="text-[12px] text-brand-muted">{mod?.tagline}</div>
        </div>
        <span className={`ml-2 mono-num text-[10px] chip ${enabled ? 'chip-emerald' : 'chip-coral'}`}>
          {enabled ? 'enabled' : 'disabled'}
        </span>
      </div>

      {/* Boundary probe */}
      <section className="mt-6 card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Boundary probe</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">
              <span className="mono-num">GET /api/companies/{"{id}"}/{moduleId}/_ping</span>
            </div>
          </div>
          {loading ? (
            <span className="mono-num text-[10px] chip">…</span>
          ) : error ? (
            <span className="mono-num text-[10px] chip chip-coral">
              <XCircle size={11} className="inline mr-0.5" />
              {error.status}
            </span>
          ) : (
            <span className="mono-num text-[10px] chip chip-emerald">
              <CheckCircle2 size={11} className="inline mr-0.5" />
              200 OK
            </span>
          )}
        </div>
        <div className="card-panel-body">
          {loading ? (
            <div className="text-brand-muted text-[12px]">Probing…</div>
          ) : error ? (
            <div>
              <div className="text-[12.5px] text-brand-coral font-medium">
                {formatApiErrorDetail(error.detail)}
              </div>
              <pre className="mt-2 mono-num text-[11px] p-3 rounded-md bg-brand-hush text-brand-ink overflow-x-auto">
{JSON.stringify(error.detail, null, 2)}
              </pre>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <BooleanCell label="Module enabled" value={enabled} />
                <BooleanCell label={`Have ${viewCap}`} value={canView} />
                <BooleanCell label="Company member" value={true} />
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[12.5px] text-brand-ink">
                Module chassis verified. Feature routes for this module will slot in behind
                <span className="mono-num text-brand-violet"> module_gate("{moduleId}")</span>.
              </div>
              <pre className="mt-2 mono-num text-[11px] p-3 rounded-md bg-brand-hush text-brand-ink overflow-x-auto">
{JSON.stringify(ping, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function BooleanCell({ label, value }) {
  return (
    <div className="rounded-md border border-brand-rule p-2">
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 mono-num text-[12px] font-semibold ${value ? 'text-emerald-600' : 'text-brand-coral'}`}>
        {value ? 'true' : 'false'}
      </div>
    </div>
  );
}
